import { Icon } from '@design-system/index'
import { colors } from '@design-system/styles'
import clsx from 'clsx'
import { Observer } from 'mobx-react'
import { useEffect } from 'react'
import {
  ERROR_REPORT_PROBLEM_CUSTOM,
  ERROR_REPORT_PROBLEM_PUBLIC,
  ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED,
  TOAST_STR,
} from 'src/constant'
import type { ErrorReport } from 'src/dto'

import { _OldButton } from '@/component/atoms/Button/_OldButton'
import { Radio } from '@/component/molecules/Radio'
import ModalTemplate from '@/component/templates/ModalTemplate/ModalTemplate'
import useSetRepository from '@/hooks/useSetRepository'
import ErrorReportService from '@/service/errorReport.service'
import modalService from '@/service/modal.service'
import { localStorageService } from '@/service/storage.service'
import { commonErrorMessage, toastSuccess } from '@/utils/toast'

import S from './ErrorReport.style'

type Props = {
  categoryId: number // 신고할 문제, 학습지, 개념 아이디
  type: 'PROBLEM_PUBLIC' | 'PROBLEM_CUSTOM' | 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED'
  onSubmitAfter?: () => void
  additionalInfo?: req.BugReportData['additionalInfo']
}

type ErrorReportType = (
  | (typeof ERROR_REPORT_PROBLEM_CUSTOM)[number]
  | (typeof ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED)[number]
  | (typeof ERROR_REPORT_PROBLEM_PUBLIC)[number]
)[]

const NEED_ANSWER_SUB_TYPE = 'ERROR_CATEGORY_NEED_ANSWER'

const ErrorReportProblem = ({ categoryId, type, onSubmitAfter, additionalInfo }: Props) => {
  const service = useSetRepository(ErrorReportService, type, categoryId, additionalInfo)

  const ERROR_REPORT_PROBLEM = (() => {
    if (type === 'PROBLEM_PUBLIC') return ERROR_REPORT_PROBLEM_PUBLIC
    if (type === 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED')
      return ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED
    return ERROR_REPORT_PROBLEM_CUSTOM
  })()

  const setAlreadyReportedPublicWorkbookToLocalStorage = () => {
    if (
      !additionalInfo?.originWorkbookId ||
      service.errorOption.subtype !== NEED_ANSWER_SUB_TYPE ||
      publicWorkbookStorageUtils.isAlreadyReported(additionalInfo.originWorkbookId)
    ) {
      return
    }
    publicWorkbookStorageUtils.setItem(additionalInfo.originWorkbookId)
  }

  const onSubmit = async () => {
    try {
      if (type === 'PROBLEM_PUBLIC' || type === 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED') {
        await service.postErrorReportPublicProblem()
        if (type === 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED') {
          setAlreadyReportedPublicWorkbookToLocalStorage()
        }
      } else {
        await service.postErrorReportCustomProblem()
      }
      modalService.closeModal()
      toastSuccess(TOAST_STR.createProblemErrorReport)
      onSubmitAfter?.()
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  useEffect(() => {
    return () => {
      service.clearErrorReport()
    }
  }, [])

  const isAlreadyReportedPublicWorkbook =
    type === 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED' &&
    Boolean(
      additionalInfo &&
        additionalInfo.originWorkbookId &&
        publicWorkbookStorageUtils.isAlreadyReported(additionalInfo.originWorkbookId),
    )

  useEffect(() => {
    if (isAlreadyReportedPublicWorkbook) {
      service.changeOption(ERROR_REPORT_PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED[0]['option'][1]['id'])
    }
  }, [])

  return (
    <Observer>
      {() => (
        <ModalTemplate
          css={S}
          header={
            <header className="error-report-header">
              <h2>
                문제 오류 신고
                <sub>빠른 시일 내로 수정하여 반영하겠습니다.</sub>
              </h2>
            </header>
          }
          body={
            <>
              <div className="error-report-description">
                <Icon name="info_fill" size={16} color={colors.gray.$700} />
                문제 수정 사항이 없을 경우 따로 연락드리지 않습니다.
              </div>
              <div
                className={clsx('error-report-body problem', type === 'PROBLEM_CUSTOM' && 'custom')}
              >
                <div className="radio-area">
                  {(ERROR_REPORT_PROBLEM as Readonly<ErrorReportType>).map((report) => (
                    <div
                      className={clsx(
                        'radio-wrap',
                        type === 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED' &&
                          report.name === '오류 종류' &&
                          'answer-not-confirmed',
                      )}
                      key={report.name}
                      data-mathflat-unique-id={report.name}
                    >
                      <p>{report.name}</p>
                      <div>
                        <Radio.Group
                          name="error"
                          className="error-report-radio-group"
                          value={service.errorOption.subtype}
                          onChange={(e) =>
                            service.changeOption(e.target.value as ErrorReport['subtype'])
                          }
                        >
                          {/* TODO - TS2349 Error */}
                          {JSON.parse(JSON.stringify(report.option)).map((item) => (
                            <div key={item.id} className="radio-wrap-container">
                              <Radio
                                value={item.id}
                                data-mathflat-unique-id={item.id}
                                disabled={
                                  item.id === NEED_ANSWER_SUB_TYPE &&
                                  isAlreadyReportedPublicWorkbook
                                }
                              >
                                {item.name}
                              </Radio>
                              {type === 'PROBLEM_PUBLIC_ANSWER_NOT_CONFIRMED' &&
                                report.name === '오류 종류' &&
                                item.id === NEED_ANSWER_SUB_TYPE && (
                                  <div
                                    className={clsx(
                                      'answer-not-confirmed-tooltip',
                                      isAlreadyReportedPublicWorkbook && 'disabled',
                                    )}
                                  >
                                    {isAlreadyReportedPublicWorkbook ? (
                                      '이미 신고접수를 해주셨어요! 정답 반영 일정이 예정되어 있지 않는 점 양해 부탁드립니다.'
                                    ) : (
                                      <>
                                        <Icon
                                          name="ai_comment_mono"
                                          size={16}
                                          color="var(--Colors-Purple-400)"
                                        />
                                        AI 분류를 통한 빠른 지원을 위해 정답은 제공되지 않으며, 반영
                                        일정이 예정되어 있지 않는 점 양해 부탁드립니다.
                                      </>
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                        </Radio.Group>
                      </div>
                    </div>
                  ))}
                </div>
                <textarea
                  onChange={(event) => service.errorOption.update({ content: event.target.value })}
                  placeholder="(선택사항) 수정 시 참고할 내용을 간단히 작성해주세요."
                  value={service.errorOption.content}
                ></textarea>
              </div>
            </>
          }
          footer={
            <div className="error-report-footer">
              <_OldButton width={120} height={40} theme="primary-caution" onClick={onSubmit}>
                신고하기
              </_OldButton>
            </div>
          }
        />
      )}
    </Observer>
  )
}

const publicWorkbookStorageUtils = {
  getItem: () => {
    return (
      localStorageService.getItem(
        'PERSIST_ALREADY_REPORTED_ANSWER_NOT_CONFIRMED_PUBLIC_WORKBOOK_IDS',
      ) ?? []
    )
  },
  isAlreadyReported: (workbookId: number) => {
    return publicWorkbookStorageUtils.getItem().includes(workbookId)
  },
  setItem: (workbookId: number) => {
    const _item = publicWorkbookStorageUtils.getItem()
    if (_item.includes(workbookId)) return
    localStorageService.setItem(
      'PERSIST_ALREADY_REPORTED_ANSWER_NOT_CONFIRMED_PUBLIC_WORKBOOK_IDS',
    )([..._item, workbookId])
  },
}

export default ErrorReportProblem
